import React from "react";
import { Trophy, Clock } from "lucide-react";
import { formatCurrency } from "./dashboardUtils";
import type { Deal, DealPipeline } from "../types";

interface DealsByPipelineCardProps {
  deals: Deal[];
  pipelines: DealPipeline[];
  wonStages: string[];
  lostStages: string[];
  currency?: string;
}

export const DealsByPipelineCard: React.FC<DealsByPipelineCardProps> = ({
  deals,
  pipelines,
  wonStages,
  lostStages,
  currency = "BRL",
}) => {
  // Agrupa dados por funil
  const pipelineStats = React.useMemo(() => {
    const stats: Record<
      string,
      {
        name: string;
        totalCount: number;
        wonCount: number;
        wonAmount: number;
        openCount: number;
        openAmount: number;
      }
    > = {};

    // Inicializa todos os funis cadastrados
    pipelines.forEach((p) => {
      const key = String(p.id).toLowerCase();
      stats[key] = {
        name: p.nome,
        totalCount: 0,
        wonCount: 0,
        wonAmount: 0,
        openCount: 0,
        openAmount: 0,
      };
    });

    deals.forEach((d) => {
      let key = String(d.funil_id || "")
        .toLowerCase()
        .trim();
      if (!key || !stats[key]) {
        // Tenta pelo primeiro funil padrão
        key = pipelines[0] ? String(pipelines[0].id).toLowerCase() : "geral";
        if (!stats[key]) {
          stats[key] = {
            name: "Funil Padrão",
            totalCount: 0,
            wonCount: 0,
            wonAmount: 0,
            openCount: 0,
            openAmount: 0,
          };
        }
      }

      stats[key].totalCount += 1;

      const isWon =
        d.status === "ganho" ||
        wonStages.some(
          (s) => s.toLowerCase() === (d.stage || "").toLowerCase(),
        );
      const isLost =
        d.status === "perdido" ||
        lostStages.some(
          (s) => s.toLowerCase() === (d.stage || "").toLowerCase(),
        );

      if (isWon) {
        stats[key].wonCount += 1;
        stats[key].wonAmount += Number(d.amount) || 0;
      } else if (!isLost) {
        stats[key].openCount += 1;
        stats[key].openAmount += Number(d.amount) || 0;
      }
    });

    return Object.values(stats);
  }, [deals, pipelines, wonStages, lostStages]);

  const totalWonAllPipes = pipelineStats.reduce(
    (acc, p) => acc + p.wonCount,
    0,
  );
  const totalOpenAllPipes = pipelineStats.reduce(
    (acc, p) => acc + p.openCount,
    0,
  );

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {/* 1. Ganhos por Funil */}
      <div className="bg-card border border-border/80 rounded-xl p-4 shadow-xs">
        <div className="flex items-center justify-between border-b border-border/50 pb-3">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-lg bg-emerald-500/10 text-emerald-500 flex items-center justify-center">
              <Trophy className="w-3.5 h-3.5" />
            </div>
            <div>
              <h3 className="text-sm font-semibold text-foreground">
                Ganhos por Funil
              </h3>
              <p className="text-[11px] text-muted-foreground">
                Negócios marcados como ganho separados por funil
              </p>
            </div>
          </div>
          <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-600 dark:text-emerald-400">
            {totalWonAllPipes} ganhos
          </span>
        </div>

        <div className="mt-4 space-y-3.5">
          {pipelineStats.length === 0 ? (
            <div className="text-center py-6 text-xs text-muted-foreground">
              Nenhum funil encontrado
            </div>
          ) : (
            pipelineStats.map((item, idx) => {
              const percent =
                totalWonAllPipes > 0
                  ? (item.wonCount / totalWonAllPipes) * 100
                  : 0;
              return (
                <div key={idx} className="space-y-1.5">
                  <div className="flex items-center justify-between text-xs">
                    <span className="font-medium text-foreground truncate max-w-[180px]">
                      {item.name}
                    </span>
                    <div className="flex items-center gap-2 font-semibold">
                      <span className="text-emerald-600 dark:text-emerald-400">
                        {item.wonCount}{" "}
                        {item.wonCount === 1 ? "venda" : "vendas"}
                      </span>
                      <span className="text-muted-foreground text-[11px]">
                        ({formatCurrency(item.wonAmount, currency)})
                      </span>
                    </div>
                  </div>
                  <div className="w-full h-2 rounded-full bg-muted overflow-hidden">
                    <div
                      className="h-full bg-emerald-500 rounded-full transition-all duration-500"
                      style={{ width: `${percent}%` }}
                    />
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* 2. Em Aberto por Funil */}
      <div className="bg-card border border-border/80 rounded-xl p-4 shadow-xs">
        <div className="flex items-center justify-between border-b border-border/50 pb-3">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-lg bg-blue-500/10 text-blue-500 flex items-center justify-center">
              <Clock className="w-3.5 h-3.5" />
            </div>
            <div>
              <h3 className="text-sm font-semibold text-foreground">
                Em Aberto por Funil
              </h3>
              <p className="text-[11px] text-muted-foreground">
                Quantidade de leads em negociação ativa por funil
              </p>
            </div>
          </div>
          <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-blue-500/10 text-blue-600 dark:text-blue-400">
            {totalOpenAllPipes} ativos
          </span>
        </div>

        <div className="mt-4 space-y-3.5">
          {pipelineStats.length === 0 ? (
            <div className="text-center py-6 text-xs text-muted-foreground">
              Nenhum funil encontrado
            </div>
          ) : (
            pipelineStats.map((item, idx) => {
              const percent =
                totalOpenAllPipes > 0
                  ? (item.openCount / totalOpenAllPipes) * 100
                  : 0;
              return (
                <div key={idx} className="space-y-1.5">
                  <div className="flex items-center justify-between text-xs">
                    <span className="font-medium text-foreground truncate max-w-[180px]">
                      {item.name}
                    </span>
                    <div className="flex items-center gap-2 font-semibold">
                      <span className="text-blue-600 dark:text-blue-400">
                        {item.openCount} leads
                      </span>
                      <span className="text-muted-foreground text-[11px]">
                        ({formatCurrency(item.openAmount, currency)})
                      </span>
                    </div>
                  </div>
                  <div className="w-full h-2 rounded-full bg-muted overflow-hidden">
                    <div
                      className="h-full bg-blue-500 rounded-full transition-all duration-500"
                      style={{ width: `${percent}%` }}
                    />
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
};
